import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { authApi } from '../../services/api';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  
  const { login, currentUser, error } = useAuth();
  const navigate = useNavigate();
  
  // Check if setup is needed
  useEffect(() => {
    const checkSetupNeeded = async () => {
      try {
        setIsLoading(true);
        
        try {
          // Try to access the setup endpoint to check if setup is needed
          // This will return 200 OK if setup is needed (no users exist)
          // or throw a 403 Forbidden error if users already exist
          await authApi.checkSetupNeeded();
          
          // If we get here, setup is needed (no users exist)
          navigate('/setup');
        } catch (setupErr) {
          // If we get a 403 error, it means users already exist
          // and we should stay on the login page
          if (setupErr.response && setupErr.response.status === 403) {
            // Users exist, stay on login page
          } else {
            // Some other error occurred, but we'll stay on login page
            console.error('Error checking if setup is needed:', setupErr);
          }
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    checkSetupNeeded();
  }, [navigate]);
  
  // Redirect if already logged in
  if (currentUser) {
    return <Navigate to="/" />;
  }
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!username || !password) {
      setLoginError('Please enter both username and password');
      return;
    }
    
    try {
      setIsLoading(true);
      setLoginError('');
      
      // Use the authApi service for login
      const response = await authApi.login(username, password);
      
      // Extract token and user data
      const { access_token, user } = response.data;
      
      // Save token to localStorage
      localStorage.setItem('token', access_token);
      
      // Update auth context
      login(user);
      
      // Redirect to dashboard on successful login
      navigate('/');
    } catch (err) {
      setLoginError(err.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="container mt-5">
      <div className="row justify-content-center">
        <div className="col-md-6 col-lg-5">
          <div className="card shadow">
            <div className="card-body p-5">
              <div className="text-center mb-4">
                <img src="/duebit-logo.png" alt="Duebit Logo" className="img-fluid" style={{ maxHeight: '100px' }} />
              </div>
              <h4 className="text-center mb-4">Login</h4>
              
              {(loginError || error) && (
                <div className="alert alert-danger" role="alert">
                  {loginError || error}
                </div>
              )}
              
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="username" className="form-label">Username</label>
                  <input
                    type="text"
                    className="form-control"
                    id="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    disabled={isLoading}
                    autoFocus
                  />
                </div>
                
                <div className="mb-4">
                  <label htmlFor="password" className="form-label">Password</label>
                  <input
                    type="password"
                    className="form-control"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
                
                <div className="d-grid">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Logging in...
                      </>
                    ) : (
                      'Login'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
          
          <div className="text-center mt-3 text-muted">
            <small>
              Default owner account: <strong>owner</strong> (see console for password)
            </small>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
